import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileText, ShieldCheck, ShieldAlert, Calendar, Download, ArrowLeft, ClipboardList, Stethoscope } from "lucide-react";
import { AttachmentsSection } from "@/components/AttachmentsSection";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "medium", timeStyle: "short" }) : "—";
const fmtDay = (iso) => iso ? new Date(iso).toLocaleDateString("pt-BR") : "—";

function StatusPill({ status }) {
  const map = {
    signed: { label: "Assinada (ICP-Brasil)", cls: "bg-teal-100 text-teal-800", Icon: ShieldCheck },
    draft: { label: "Rascunho — sem validade legal", cls: "bg-amber-100 text-amber-900", Icon: ShieldAlert },
    deleted: { label: "Excluída", cls: "bg-red-100 text-red-800", Icon: ShieldAlert },
  };
  const s = map[status] || map.draft;
  return <span className={`text-xs px-2 py-1 rounded-full inline-flex items-center gap-1 ${s.cls}`}><s.Icon className="w-3 h-3" />{s.label}</span>;
}

export default function Chart() {
  const { patientId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [chart, setChart] = useState(null);
  const [loading, setLoading] = useState(true);

  const targetId = patientId || user?.id;

  const loadChart = useCallback(async () => {
    if (!targetId) return;
    try {
      const { data } = await api.get(`/chart/patient/${targetId}`);
      setChart(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  }, [targetId]);

  useEffect(() => { loadChart(); }, [loadChart]);

  if (loading) return <div className="container-optia py-16 text-slate-500">Carregando prontuário…</div>;
  if (!chart) return <div className="container-optia py-16 text-slate-500">Prontuário indisponível.</div>;

  const p = chart.patient;
  const preAna = chart.pre_anamnesis;

  return (
    <div className="container-optia py-10 animate-fade-in max-w-5xl">
      {patientId && user?.role === "professional" && (
        <button onClick={()=>nav(-1)} className="text-sm text-slate-500 hover:text-teal-700 mb-4 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" />Voltar</button>
      )}
      <div className="flex items-start gap-4">
        <div className="h-14 w-14 rounded-2xl bg-teal-100 grid place-items-center text-teal-700 shrink-0"><ClipboardList className="w-6 h-6" /></div>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-2xl font-semibold text-navy-900" data-testid="chart-patient-name">{p.name}</h1>
          <div className="text-sm text-slate-600 mt-1 flex flex-wrap gap-3">
            <span>{p.email}</span>
            {p.cpf && <span>CPF: {p.cpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2}).*/, "$1.$2.$3-$4")}</span>}
            {p.municipality_name && <span className="text-teal-700">SUS · {p.municipality_name}</span>}
          </div>
        </div>
        <Button variant="outline" onClick={async () => {
          try {
            const token = localStorage.getItem("optia_token");
            const res = await fetch(`${BACKEND_URL}/api/chart/patient/${targetId}/export`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Falha ao gerar PDF");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const slug = (p.name || "paciente").toLowerCase().replace(/\s+/g, "-").slice(0, 40);
            a.download = `prontuario-${slug}-${new Date().toISOString().slice(0,10).replace(/-/g,"")}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Prontuário PDF baixado");
          } catch (err) { toast.error(err.message); }
        }} className="border-navy-900 text-navy-900" data-testid="chart-export-pdf">
          <Download className="w-4 h-4 mr-1" />Baixar prontuário (PDF)
        </Button>
      </div>

      {/* Pré-anamnese */}
      <Card className="card-soft mt-6" data-testid="chart-pre-anamnesis">
        <CardContent className="p-6">
          <h2 className="font-display text-lg font-semibold text-navy-900 flex items-center gap-2"><ClipboardList className="w-4 h-4 text-teal-600" />Pré-anamnese</h2>
          {!preAna || !preAna.data ? (
            <p className="text-sm text-slate-500 mt-3">Ainda não preenchida.</p>
          ) : (
            <>
              <div className="text-xs text-slate-500 mt-1">
                {preAna.completed ? `Enviada em ${fmtDate(preAna.updated_at)}` : `Rascunho — última atualização ${fmtDate(preAna.updated_at)}`}
              </div>
              <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {[
                  ["Nome da criança", preAna.data.nome_completo],
                  ["Idade", preAna.data.idade],
                  ["Data de nascimento", preAna.data.data_nascimento],
                  ["Cidade/Estado", preAna.data.cidade_estado],
                  ["Responsável", preAna.data.responsavel_preenchimento],
                  ["Motivo da busca", preAna.data.motivo_procura],
                  ["Diagnósticos prévios", preAna.data.diagnosticos],
                  ["Medicações", preAna.data.medicacoes],
                  ["Alergias", preAna.data.alergias],
                  ["Sensibilidades", preAna.data.sensibilidades],
                ].map(([k, v]) => v ? (
                  <div key={k}><div className="text-xs text-slate-500">{k}</div><div className="text-slate-900">{v}</div></div>
                ) : null)}
              </div>
              {user?.role === "patient" && <Button variant="outline" size="sm" onClick={()=>nav("/pre-anamnese")} className="mt-4" data-testid="chart-edit-preana">Editar ficha completa</Button>}
            </>
          )}
        </CardContent>
      </Card>

      {/* Consultas */}
      <Card className="card-soft mt-5" data-testid="chart-appointments">
        <CardContent className="p-6">
          <h2 className="font-display text-lg font-semibold text-navy-900 flex items-center gap-2"><Calendar className="w-4 h-4 text-teal-600" />Consultas</h2>
          {chart.appointments.length === 0 ? (
            <p className="text-sm text-slate-500 mt-3">Nenhuma consulta registrada.</p>
          ) : (
            <div className="mt-4 space-y-3">
              {chart.appointments.map((a) => (
                <div key={a.id} className="border border-slate-200 rounded-xl p-4" data-testid={`chart-appt-${a.id}`}>
                  <div className="flex flex-wrap justify-between gap-2">
                    <div>
                      <div className="font-medium text-navy-900">{a.professional_name} <span className="text-slate-500 text-sm font-normal">— {a.professional_specialty}</span></div>
                      <div className="text-xs text-slate-500 mt-0.5">{fmtDate(a.scheduled_at)}</div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${a.status === "completed" ? "bg-navy-900 text-white" : a.status === "confirmed" ? "bg-teal-100 text-teal-800" : a.status === "cancelled" ? "bg-red-100 text-red-800" : "bg-slate-200 text-slate-700"}`}>{a.status}</span>
                  </div>
                  {a.reason && <p className="text-sm text-slate-600 mt-2">Motivo: {a.reason}</p>}
                  {a.professional_notes && user?.role !== "patient" && (
                    <div className="mt-3 bg-slate-50 rounded-lg p-3 text-sm text-slate-800 whitespace-pre-wrap"><b className="text-navy-900 text-xs">Nota clínica:</b><br />{a.professional_notes}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Prescrições */}
      <Card className="card-soft mt-5" data-testid="chart-prescriptions">
        <CardContent className="p-6">
          <h2 className="font-display text-lg font-semibold text-navy-900 flex items-center gap-2"><FileText className="w-4 h-4 text-teal-600" />Prescrições</h2>
          {chart.prescriptions.length === 0 ? (
            <p className="text-sm text-slate-500 mt-3">Nenhuma prescrição registrada.</p>
          ) : (
            <div className="mt-4 space-y-3">
              {chart.prescriptions.map((r) => (
                <div key={r.id} className={`border rounded-xl p-4 ${r.status === "deleted" ? "border-red-200 bg-red-50/40 opacity-70" : "border-slate-200"}`} data-testid={`chart-presc-${r.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="font-medium text-navy-900">{r.professional_name}{r.professional_crm ? ` — CRM ${r.professional_crm}` : ""}</div>
                      <div className="text-xs text-slate-500">{fmtDate(r.created_at)}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusPill status={r.status} />
                      {r.memed_pdf_url && r.status === "signed" && (
                        <a href={r.memed_pdf_url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" data-testid={`chart-presc-pdf-${r.id}`}><Download className="w-3 h-3 mr-1" />PDF</Button></a>
                      )}
                    </div>
                  </div>
                  <ul className="mt-3 list-disc pl-5 text-sm">
                    {r.medications.map((m, i) => <li key={`${r.id}-${m.name}-${i}`}><b>{m.name}</b> — {m.dosage}{m.frequency ? ` · ${m.frequency}` : ""}{m.duration ? ` · ${m.duration}` : ""}</li>)}
                  </ul>
                  {r.cid_codes?.length > 0 && <div className="text-xs text-slate-600 mt-2">CID: {r.cid_codes.join(", ")}</div>}
                  {r.clinical_notes && <div className="text-sm text-slate-700 mt-2">{r.clinical_notes}</div>}
                  {r.status === "deleted" && <div className="text-xs text-red-700 mt-2 font-medium">Documento excluído no Memed — não deve ser utilizado.</div>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AttachmentsSection
        patientId={targetId}
        currentUser={user}
        initialAttachments={chart.attachments || []}
      />

      <p className="text-xs text-slate-400 mt-6 text-center">Prontuário gerado em {fmtDate(chart.generated_at)} · Optia Saúde · Uso restrito</p>
    </div>
  );
}
